import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Filter } from "lucide-react";
import { useAuth } from "../../services/AuthContext";
import { getCheckoutErrors, getCheckoutFunnel } from "../../services/admin";
import {
  CHECKOUT_FUNNEL_LABELS,
  CHECKOUT_PAYMENT_SPLIT_STEPS,
  CHECKOUT_VALIDATION_LABELS,
  defaultAdminDateRange,
  formatInteger,
  formatPercent,
} from "../../config/admin";
import DateRangeBar from "./DateRangeBar";
import AdminReloadOverlay from "./AdminReloadOverlay";

const PAYMENT_SPLIT_NAMES = new Set(
  CHECKOUT_PAYMENT_SPLIT_STEPS.map((item) => item.eventName)
);

function groupFunnelRows(steps) {
  const byName = Object.fromEntries(steps.map((step) => [step.event_name, step]));
  const rows = [];
  const consumed = new Set();

  steps.forEach((step) => {
    if (consumed.has(step.event_name)) return;

    if (PAYMENT_SPLIT_NAMES.has(step.event_name)) {
      const segments = CHECKOUT_PAYMENT_SPLIT_STEPS.map((item) => ({
        ...item,
        step: byName[item.eventName] || {
          event_name: item.eventName,
          unique_visitors: 0,
          conversion_from_previous: null,
          conversion_from_first: 0,
        },
      }));
      segments.forEach((segment) => consumed.add(segment.eventName));
      rows.push({ type: "split", key: "payment-split", segments });
      return;
    }

    consumed.add(step.event_name);
    rows.push({ type: "single", key: step.event_name, step });
  });

  return rows;
}

export default function AdminCheckoutPage() {
  const { getIdToken } = useAuth();
  const initialRange = defaultAdminDateRange(30);
  const [range, setRange] = useState(initialRange);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [steps, setSteps] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [apiErrors, setApiErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCheckout = useCallback(async (rangeOverride = null) => {
    const rangeToUse = rangeOverride || appliedRange;
    try {
      setLoading(true);
      setError("");
      const token = await getIdToken();
      const [funnelData, errorsData] = await Promise.all([
        getCheckoutFunnel(token, rangeToUse),
        getCheckoutErrors(token, rangeToUse).catch(() => ({
          validation_errors: [],
          api_errors: [],
        })),
      ]);
      setSteps(funnelData.steps || []);
      setValidationErrors(errorsData.validation_errors || []);
      setApiErrors(errorsData.api_errors || []);
    } catch (err) {
      setError(err.message || "Não foi possível carregar o funil de checkout.");
    } finally {
      setLoading(false);
    }
  }, [appliedRange, getIdToken]);

  useEffect(() => {
    loadCheckout();
  }, [loadCheckout]);

  const firstCount = steps[0]?.unique_visitors || 0;

  return (
    <div className="admin-page">
      <AdminReloadOverlay visible={loading} message="Atualizando funil..." />
      <div className="admin-page-header">
        <h1 className="admin-title">
          <Filter size={30} className="admin-title-icon" />
          Funil de checkout
        </h1>
        <p className="admin-subtitle">
          Visitantes únicos em cada etapa do checkout no período selecionado.
        </p>
      </div>

      <DateRangeBar
        startDate={range.startDate}
        endDate={range.endDate}
        onChange={setRange}
        onApply={(next) => {
          setAppliedRange(next);
          setRange(next);
        }}
        onReload={(next) => {
          setRange(next);
          if (
            next.startDate === appliedRange.startDate &&
            next.endDate === appliedRange.endDate
          ) {
            loadCheckout(next);
          } else {
            setAppliedRange(next);
          }
        }}
        loading={loading}
      />

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-panel">
        <h2 className="admin-panel-title">Progressão do funil</h2>
        {steps.length === 0 && !loading && (
          <div className="admin-empty">Nenhum evento de checkout neste período.</div>
        )}
        {steps.length > 0 && (
          <div className="admin-funnel">
            {groupFunnelRows(steps).map((row) => {
              if (row.type === "split") {
                const segments = row.segments.map((segment) => {
                  const width =
                    firstCount > 0
                      ? (segment.step.unique_visitors / firstCount) * 100
                      : 0;
                  return { ...segment, width };
                });
                const totalWidth = segments.reduce((sum, segment) => sum + segment.width, 0);
                const scale = totalWidth > 100 ? 100 / totalWidth : 1;

                return (
                  <div key={row.key} className="admin-funnel-row">
                    <div className="admin-funnel-meta">
                      <div className="admin-funnel-legend">
                        {segments.map((segment) => (
                          <div key={segment.eventName} className="admin-funnel-legend-item">
                            <p className="admin-funnel-label">
                              <span
                                className="admin-funnel-swatch"
                                style={{ background: segment.color }}
                              />
                              {CHECKOUT_FUNNEL_LABELS[segment.eventName] || segment.eventName}
                            </p>
                            <p className="admin-funnel-sub">
                              {formatInteger(segment.step.unique_visitors)} visitantes
                              {segment.step.conversion_from_previous != null && (
                                <>
                                  {" "}
                                  · {formatPercent(segment.step.conversion_from_previous)} da
                                  etapa anterior
                                </>
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="admin-funnel-visual">
                      <div className="admin-funnel-track">
                        {totalWidth > 0 && (
                          <div className="admin-funnel-bar-split">
                            {segments.map((segment) =>
                              segment.width > 0 ? (
                                <div
                                  key={segment.eventName}
                                  className="admin-funnel-bar-segment"
                                  style={{
                                    width: `${segment.width * scale}%`,
                                    background: segment.color,
                                  }}
                                />
                              ) : null
                            )}
                          </div>
                        )}
                      </div>
                      <div className="admin-funnel-count admin-funnel-count-stack">
                        {segments.map((segment) => (
                          <span key={segment.eventName}>
                            {formatInteger(segment.step.unique_visitors)} ·{" "}
                            {formatPercent(segment.step.conversion_from_first)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              const step = row.step;
              const width =
                firstCount > 0 ? (step.unique_visitors / firstCount) * 100 : 0;
              return (
                <div key={row.key} className="admin-funnel-row">
                  <div className="admin-funnel-meta">
                    <p className="admin-funnel-label">
                      {CHECKOUT_FUNNEL_LABELS[step.event_name] || step.event_name}
                    </p>
                    <p className="admin-funnel-sub">
                      {formatInteger(step.unique_visitors)} visitantes
                      {step.conversion_from_previous != null && (
                        <> · {formatPercent(step.conversion_from_previous)} da etapa anterior</>
                      )}
                    </p>
                  </div>
                  <div className="admin-funnel-visual">
                    <div className="admin-funnel-track">
                      {width > 0 && (
                        <div className="admin-funnel-bar" style={{ width: `${width}%` }} />
                      )}
                    </div>
                    <div className="admin-funnel-count">
                      {formatInteger(step.unique_visitors)} · {formatPercent(step.conversion_from_first)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="admin-panel">
        <h2 className="admin-panel-title">
          <AlertTriangle size={18} /> Erros comuns no checkout
        </h2>
        <p className="admin-muted" style={{ marginBottom: 16 }}>
          Eventos registrados no checkout no mesmo período.
        </p>

        <h3 className="admin-section-title">Validação de campos (front-end)</h3>
        {validationErrors.length === 0 ? (
          <div className="admin-empty">Nenhum erro de validação neste período.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Campo</th>
                  <th>Evento</th>
                  <th>Ocorrências</th>
                  <th>Visitantes</th>
                </tr>
              </thead>
              <tbody>
                {validationErrors.map((row) => (
                  <tr key={row.event_name}>
                    <td>
                      <strong>
                        {CHECKOUT_VALIDATION_LABELS[row.field] || row.field || "—"}
                      </strong>
                    </td>
                    <td className="admin-muted">{row.event_name}</td>
                    <td>{formatInteger(row.event_count)}</td>
                    <td>{formatInteger(row.unique_visitors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h3 className="admin-section-title" style={{ marginTop: 22 }}>
          Erros de API
        </h3>
        {apiErrors.length === 0 ? (
          <div className="admin-empty">Nenhum erro de API neste período.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Método</th>
                  <th>Rota</th>
                  <th>Ocorrências</th>
                  <th>Visitantes</th>
                </tr>
              </thead>
              <tbody>
                {apiErrors.map((row) => (
                  <tr key={row.event_name}>
                    <td>
                      <span className="admin-status admin-status-overdue">
                        {row.status ?? "—"}
                      </span>
                    </td>
                    <td>{row.method || "—"}</td>
                    <td>
                      <code className="admin-route">{row.route || row.event_name}</code>
                    </td>
                    <td>{formatInteger(row.event_count)}</td>
                    <td>{formatInteger(row.unique_visitors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
